/**
 * Pure helpers for client-side table sorting used by ContentRendererComponent.
 *
 * The component owns the reactive sort state (a signal); this module owns the
 * sort algorithm and value-type detection so the component stays focused on
 * rendering.
 */

/** Direction for a table column sort. */
export type TableSortDirection = 'asc' | 'desc';

/**
 * Snapshot of table sort state.
 * `col === null` means no sort is applied — rows render in their original order.
 */
export interface TableSortState {
  col: number | null;
  dir: TableSortDirection;
}

/** Initial / "no sort applied" state. */
export const DEFAULT_TABLE_SORT: TableSortState = { col: null, dir: 'asc' };

/**
 * Parses values like `"42"`, `"-1.5"`, `"1,234.56"`, `"50%"`, `"12.5 %"`.
 * Returns null for anything that isn't a recognisable number.
 */
export function parseNumeric(value: string): number | null {
  const cleaned = value.replace(/[,\s]/g, '').replace(/%$/, '');
  if (!/^-?\d+(\.\d+)?$/.test(cleaned)) return null;
  return parseFloat(cleaned);
}

/**
 * Returns true if the value parses as a date and isn't already a plain number /
 * percentage. Uses structural cues (separators or month-name letters) to avoid
 * `Date.parse` false positives on bare integers like "2024".
 */
export function isDateLike(value: string): boolean {
  if (parseNumeric(value) !== null) return false;
  if (!/[\/\-]|[a-z]/i.test(value)) return false;
  return !isNaN(Date.parse(value));
}

/**
 * Returns rows sorted by the requested column. The column's value type
 * (date / numeric / string) is detected once across all non-empty cells so a
 * single comparator drives the entire sort. Empty cells sort to the end
 * regardless of direction.
 *
 * Returns the input rows unchanged when `state.col` is null.
 */
export function applyTableSort(rows: string[][], state: TableSortState): string[][] {
  const { col, dir } = state;
  if (col === null) return rows;

  const values = rows.map(r => String(r?.[col] ?? '').trim());
  const nonEmpty = values.filter(v => v !== '');
  const allNumeric = nonEmpty.length > 0 && nonEmpty.every(v => parseNumeric(v) !== null);
  const allDates = !allNumeric && nonEmpty.length > 0 && nonEmpty.every(v => isDateLike(v));

  return [...rows].sort((a, b) => {
    const av = String(a?.[col] ?? '').trim();
    const bv = String(b?.[col] ?? '').trim();
    if (av === '' && bv === '') return 0;
    if (av === '') return 1;
    if (bv === '') return -1;

    let cmp: number;
    if (allDates) {
      cmp = Date.parse(av) - Date.parse(bv);
    } else if (allNumeric) {
      cmp = (parseNumeric(av) ?? 0) - (parseNumeric(bv) ?? 0);
    } else {
      // Natural sort: { numeric: true } treats embedded digit runs as numbers,
      // so mixed-content columns sort intuitively — e.g. "Site 2" < "Site 10",
      // "100" < "20" stays correct, and pure-text rows still sort alphabetically.
      cmp = av.localeCompare(bv, undefined, { numeric: true });
    }
    return dir === 'asc' ? cmp : -cmp;
  });
}

/**
 * Returns the next sort state given the current state and a clicked column.
 * Cycle on the same column: asc -> desc -> unsorted (default order).
 * Clicking a different column resets to ascending on that column.
 */
export function nextTableSortState(current: TableSortState, colIndex: number): TableSortState {
  if (current.col !== colIndex) return { col: colIndex, dir: 'asc' };
  if (current.dir === 'asc') return { col: colIndex, dir: 'desc' };
  return DEFAULT_TABLE_SORT;
}

/** Returns the visual indicator (`↕` `↑` `↓`) for a header cell. */
export function tableSortIcon(state: TableSortState, colIndex: number): string {
  if (state.col !== colIndex) return '↕';
  return state.dir === 'asc' ? '↑' : '↓';
}

/** Returns the `aria-sort` attribute value for a header cell. */
export function tableSortAria(state: TableSortState, colIndex: number): 'ascending' | 'descending' | 'none' {
  if (state.col !== colIndex) return 'none';
  return state.dir === 'asc' ? 'ascending' : 'descending';
}
